import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { message } from "antd";
import { FaRegAddressCard, FaMagnifyingGlass } from "react-icons/fa6";
import { BiDetail, BiEdit, BiToggleLeft, BiToggleRight } from "react-icons/bi";

import {
  listarConcessionarias,
  listarGruposDealerAtivos,
  alterarStatusConcessionaria,
  extrairRelatorioConcessionarias,
} from "../../services/http/cadastrosService";
import { saveAxiosBlobAs } from "../../helpers/saveAxiosBlobAs";
import { cnpj as mascaraCnpj } from "../../services/mask";

const ITENS_POR_PAGINA = 10;

/** Aba "Concessionárias" da tela de Cadastros (RD 95133). */
export default function ListaConcessionarias() {
  const history = useHistory();

  const [lista, setLista] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const [busca, setBusca] = useState("");
  const [grupoFiltro, setGrupoFiltro] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [buscaTimeout, setBuscaTimeout] = useState(null);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);

  const carregarLista = async (
    filtro = busca,
    grupoDealerId = grupoFiltro,
    status = statusFiltro,
    pagina = 1
  ) => {
    setCarregando(true);
    setErro(null);

    try {
      const resposta = await listarConcessionarias(
        pagina,
        ITENS_POR_PAGINA,
        filtro,
        grupoDealerId,
        status
      );

      if (resposta.fail) {
        setLista([]);
        setTotalItens(0);
        setTotalPaginas(0);
        setErro(
          resposta.errors?.message || "Erro ao carregar as concessionárias"
        );
        return;
      }

      setLista(resposta.data?.data ?? []);
      setTotalItens(resposta.data?.totalCount ?? 0);
      setTotalPaginas(resposta.data?.totalPages ?? 0);
    } catch (e) {
      console.error("Erro ao carregar concessionárias:", e);
      setErro("Erro ao carregar as concessionárias");
    } finally {
      setCarregando(false);
    }
  };

  const carregarGrupos = async () => {
    const resposta = await listarGruposDealerAtivos();
    if (!resposta.fail) {
      setGrupos(resposta.data ?? []);
    }
  };

  useEffect(() => {
    carregarGrupos();
    carregarLista("", "", "", 1);
  }, []);

  useEffect(() => {
    return () => {
      if (buscaTimeout) clearTimeout(buscaTimeout);
    };
  }, [buscaTimeout]);

  const aoBuscar = (evento) => {
    const valor = evento.target.value;
    setBusca(valor);
    setPaginaAtual(1);

    clearTimeout(buscaTimeout);
    setBuscaTimeout(
      setTimeout(() => carregarLista(valor, grupoFiltro, statusFiltro, 1), 500)
    );
  };

  const aoFiltrarGrupo = (evento) => {
    const valor = evento.target.value;
    setGrupoFiltro(valor);
    setPaginaAtual(1);
    carregarLista(busca, valor, statusFiltro, 1);
  };

  const aoFiltrarStatus = (evento) => {
    const valor = evento.target.value;
    setStatusFiltro(valor);
    setPaginaAtual(1);
    carregarLista(busca, grupoFiltro, valor, 1);
  };

  const aoTrocarPagina = (pagina) => {
    setPaginaAtual(pagina);
    carregarLista(busca, grupoFiltro, statusFiltro, pagina);
  };

  // Sem modal de confirmação: alterna direto e informa pela notificação.
  const aoAlterarStatus = async (concessionaria) => {
    const ativar = concessionaria.status !== "Ativa";

    const resposta = await alterarStatusConcessionaria(concessionaria.id, ativar);

    if (resposta.fail) {
      message.error(
        resposta.errors?.message || "Não foi possível alterar o status"
      );
      return;
    }

    message.success(
      ativar
        ? `${concessionaria.nomeFantasia} ativada`
        : `${concessionaria.nomeFantasia} desativada`
    );
    carregarLista(busca, grupoFiltro, statusFiltro, paginaAtual);
  };

  const aoExtrairRelatorio = async () => {
    try {
      setCarregando(true);
      const resposta = await extrairRelatorioConcessionarias(
        busca,
        grupoFiltro,
        statusFiltro
      );
      saveAxiosBlobAs(resposta, "relatorio-concessionarias.xlsx");
    } catch (e) {
      console.error("Erro ao extrair relatório:", e);
      message.error("Não foi possível extrair o relatório");
    } finally {
      setCarregando(false);
    }
  };

  return (
    <>
      <div className="cadastros-title-block">
        <h2>Concessionárias</h2>
        <p>Gerencie as concessionárias cadastradas no sistema.</p>
      </div>

      <div
        className="filters-section p-3"
        style={{
          backgroundColor: "#E4E4E483",
          borderRadius: "10px",
          marginBottom: "20px",
        }}
      >
        <div className="row g-3 pb-4">
          <div className="col-12 col-md-4">
            <label
              htmlFor="buscaConcessionaria"
              className="form-label fw-semibold text-secondary"
            >
              Buscar Concessionária
            </label>
            <input
              type="text"
              id="buscaConcessionaria"
              className="form-control"
              style={{
                height: "42px",
                fontSize: "14px",
                border: "1px solid #b1b1b1",
                borderRadius: "5px",
                color: "#495057",
              }}
              placeholder="Nome, código dealer, razão social ou CNPJ..."
              value={busca}
              onChange={aoBuscar}
            />
          </div>

          <div className="d-flex flex-column col-12 col-md-2">
            <label
              htmlFor="grupoDealerSelect"
              className="form-label fw-semibold text-secondary"
            >
              Grupo Dealer
            </label>
            <select
              id="grupoDealerSelect"
              className="form-select"
              value={grupoFiltro}
              onChange={aoFiltrarGrupo}
              style={{
                height: "42px",
                fontSize: "14px",
                border: "1px solid #b1b1b1",
                borderRadius: "5px",
                color: "#495057",
              }}
            >
              <option value="">Todos os grupos</option>
              {grupos.map((grupo) => (
                <option key={grupo.id} value={grupo.id}>
                  {grupo.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex flex-column col-12 col-md-2">
            <label
              htmlFor="statusConcessionariaSelect"
              className="form-label fw-semibold text-secondary"
            >
              Status
            </label>
            <select
              id="statusConcessionariaSelect"
              className="form-select"
              value={statusFiltro}
              onChange={aoFiltrarStatus}
              style={{
                height: "42px",
                fontSize: "14px",
                border: "1px solid #b1b1b1",
                borderRadius: "5px",
                color: "#495057",
              }}
            >
              <option value="">Todos os status</option>
              <option value="Ativa">Ativa</option>
              <option value="Inativa">Inativa</option>
            </select>
          </div>

          <div className="col-12 col-md-2 d-flex align-items-end mt-3">
            <button
              className="btn btn-secondary w-100"
              onClick={aoExtrairRelatorio}
              disabled={carregando}
              style={{ height: "42px" }}
            >
              <FaMagnifyingGlass size={18} className="me-2" />
              {carregando ? "Extraindo..." : "Extrair Relatório"}
            </button>
          </div>

          <div className="col-12 col-md-2 d-flex align-items-end">
            <button
              className="btn btn-primary w-100"
              onClick={() => history.push("/cadastros/concessionarias/nova")}
              style={{ height: "42px" }}
            >
              <FaRegAddressCard size={18} className="me-2" />
              Nova Concessionária
            </button>
          </div>
        </div>
      </div>

      {carregando ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden"></span>
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="participants-table">
            <thead>
              <tr>
                <th>Código Dealer</th>
                <th>Razão Social</th>
                <th>Nome Fantasia</th>
                <th>CNPJ</th>
                <th>Grupo Dealer</th>
                <th>Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.length > 0 ? (
                lista.map((concessionaria) => (
                  <tr key={concessionaria.id}>
                    <td>{concessionaria.codigoDealer}</td>
                    <td>{concessionaria.razaoSocial}</td>
                    <td>{concessionaria.nomeFantasia}</td>
                    <td>{mascaraCnpj(concessionaria.cnpj)}</td>
                    <td>{concessionaria.grupoDealer || "-"}</td>
                    <td>
                      <span
                        className={`status ${
                          concessionaria.status === "Ativa" ? "ativa" : "inativa"
                        }`}
                      >
                        {concessionaria.status}
                      </span>
                    </td>
                    <td>
                      <div className="cadastros-acoes">
                        <button
                          type="button"
                          title="Visualizar"
                          onClick={() =>
                            history.push(
                              `/cadastros/concessionarias/visualizar/${concessionaria.id}`
                            )
                          }
                        >
                          <BiDetail size={22} />
                        </button>
                        <button
                          type="button"
                          title="Editar"
                          onClick={() =>
                            history.push(
                              `/cadastros/concessionarias/editar/${concessionaria.id}`
                            )
                          }
                        >
                          <BiEdit size={22} />
                        </button>
                        <button
                          type="button"
                          title={
                            concessionaria.status === "Ativa"
                              ? "Desativar"
                              : "Ativar"
                          }
                          onClick={() => aoAlterarStatus(concessionaria)}
                        >
                          {concessionaria.status === "Ativa" ? (
                            <BiToggleRight size={34} />
                          ) : (
                            <BiToggleLeft size={34} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="cadastros-empty">
                      {erro || "Nenhuma concessionária encontrada"}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPaginas > 0 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => aoTrocarPagina(paginaAtual - 1)}
            disabled={paginaAtual <= 1}
          >
            Anterior
          </button>

          <div className="pagination-numbers">
            {[...Array(totalPaginas)].map((_, indice) => (
              <button
                key={indice + 1}
                className={`pagination-number ${
                  paginaAtual === indice + 1 ? "active" : ""
                }`}
                onClick={() => aoTrocarPagina(indice + 1)}
              >
                {indice + 1}
              </button>
            ))}
          </div>

          <button
            className="pagination-button"
            onClick={() => aoTrocarPagina(paginaAtual + 1)}
            disabled={paginaAtual >= totalPaginas}
          >
            Próximo
          </button>
        </div>
      )}

      {totalItens > 0 && (
        <p className="text-muted" style={{ fontSize: "12px" }}>
          {totalItens} registro(s) encontrado(s)
        </p>
      )}
    </>
  );
}
